// CJ affiliate-link wrappers for the marketing site. Mirrors the helper in
// frontend/src/accommodationSearch.ts so the same CJ deep-link base can be
// set once at build time (REACT_APP_CJ_BOOKING_BASE) and used in both apps.
//
// Usage on an outbound Booking.com link:
//
//   <a
//       href={wrapBookingWithCj(
//           "https://www.booking.com/hotel/fr/somewhere.html",
//           "grand_tour_2026_day1", // optional sid for CJ attribution
//       )}
//       target="_blank"
//       rel="noopener noreferrer"
//   >Booking.com</a>
//
// When REACT_APP_CJ_BOOKING_BASE is unset (e.g. local dev without affiliate
// creds) the helper returns the raw destination so the link still works,
// and prints a one-time console warning so the missing env var is loud.

const CJ_BOOKING_BASE: string =
    (process.env.REACT_APP_CJ_BOOKING_BASE || "").trim();

if (
    !CJ_BOOKING_BASE &&
    typeof process !== "undefined" &&
    process.env.NODE_ENV !== "production"
) {
    // eslint-disable-next-line no-console
    console.warn(
        "[CJ] REACT_APP_CJ_BOOKING_BASE is not set. " +
            "Booking.com affiliate links on the marketing site will fall back " +
            "to raw URLs and clicks will NOT be tracked in the CJ dashboard.",
    );
}

export const wrapBookingWithCj = (destination: string, sid?: string): string => {
    if (!CJ_BOOKING_BASE) return destination;
    try {
        const url = new URL(CJ_BOOKING_BASE);
        url.searchParams.set("url", destination);
        if (sid) url.searchParams.set("sid", sid);
        return url.toString();
    } catch {
        // Malformed base — fall back to the raw destination so the link
        // still works.
        return destination;
    }
};

// Hotels.com is on CJ too, with its own click-base.
const CJ_HOTELS_BASE: string =
    (process.env.REACT_APP_CJ_HOTELS_BASE || "").trim();

// Generic CJ wrapper for advertisers other than Booking.com.
const wrapWithCj = (base: string, destination: string, sid?: string): string => {
    if (!base) return destination;
    try {
        const url = new URL(base);
        url.searchParams.set("url", destination);
        if (sid) url.searchParams.set("sid", sid);
        return url.toString();
    } catch {
        return destination;
    }
};

// booking.com resolves a town/city or hotel name, not a full street address.
// Handed a full formatted address it opens with an empty search box, so drop a
// leading street line. (Mirrors searchLocation in frontend/accommodationSearch.)
// Trailing UK-style postcode (e.g. "IV54 8LR", "SW1A 1AA") — booking.com fails
// to resolve a locality when one is tacked on, so drop it.
const UK_POSTCODE_TAIL = /\s+[A-Za-z]{1,2}\d[A-Za-z\d]?\s+\d[A-Za-z]{2}\s*$/;

export const searchLocation = (query: string): string => {
    const raw = (query || "").trim();
    if (!raw) return raw;
    const parts = raw
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
    let loc = raw;
    if (parts.length > 1) {
        const looksLikeStreet = /^\d/.test(parts[0]) || parts.length >= 3;
        loc = (looksLikeStreet ? parts.slice(1) : parts).join(", ");
    }
    loc = loc.replace(UK_POSTCODE_TAIL, "").trim();
    return loc || raw;
};

// Build a CJ-wrapped Booking.com search link for a hotel name or area. Returns
// null when there's nothing to search.
export const bookingSearchUrl = (query: string, sid?: string): string | null => {
    const location = searchLocation(query);
    if (!location) return null;
    const params = new URLSearchParams({
        ss: location,
        ssne: location,
        ssne_untouched: location,
        group_adults: "2",
        no_rooms: "1",
        group_children: "0",
        lang: "en-gb",
    });
    const destination = `https://www.booking.com/searchresults.html?${params.toString()}`;
    return wrapBookingWithCj(destination, sid);
};

// Hotels.com search (CJ-wrapped when REACT_APP_CJ_HOTELS_BASE is set).
export const hotelsSearchUrl = (query: string, sid?: string): string | null => {
    const location = searchLocation(query);
    if (!location) return null;
    const params = new URLSearchParams({ destination: location });
    const destination = `https://www.hotels.com/Hotel-Search?${params.toString()}`;
    return wrapWithCj(CJ_HOTELS_BASE, destination, sid);
};

// Pitchup (campsites) — no affiliate programme, so a plain search link.
export const pitchupSearchUrl = (query: string): string | null => {
    const location = searchLocation(query);
    if (!location) return null;
    const params = new URLSearchParams({ q: location });
    return `https://www.pitchup.com/search/?${params.toString()}`;
};

// Airbnb — plain search link.
export const airbnbSearchUrl = (query: string): string | null => {
    const location = searchLocation(query);
    if (!location) return null;
    return `https://www.airbnb.com/s/${encodeURIComponent(location)}/homes`;
};
